/**
 * src/components/layout/SaveConflictBanner.jsx
 *
 * App-shell banner shown when a save could not take effect because someone
 * else changed the same thing first.
 *
 * Since CONC-01 (GH #7817) a save only clashes on the same FIELD: a change by
 * another session anywhere else in the workspace never reaches this banner.
 * usePersistence fires 'seasuite:state-conflict' with `detail.conflicts` — one
 * entry per clashing field, carrying both values — and keeps everything of the
 * save that did not clash. The banner lists each clash with the other person's
 * value and the user's own, so nothing the user typed disappears from view,
 * and offers "Keep mine" where re-applying it is safe (patchConflicts.js). A
 * conflict event without that detail (a write path that has not moved to
 * patches yet) shows the general notice, as before.
 *
 * Never destructive by itself: dismissing keeps the other person's value,
 * which is already what is stored.
 */

import { useState, useEffect, useCallback } from 'react';

function show(value) {
  if (value === undefined) return '(empty)';
  if (typeof value === 'string') return value || '(empty)';
  try {
    const s = JSON.stringify(value);
    return s.length > 160 ? `${s.slice(0, 157)}…` : s;
  } catch {
    return String(value);
  }
}

const ACTION_LABEL = {
  keep: 'Keep mine',
  delete: 'Delete anyway',
  restore: 'Restore with my edit',
};

const valueStyle = {
  fontFamily: 'var(--font-mono)',
  background: 'var(--bg2)',
  color: 'var(--fg)',
  padding: '1px 6px',
  borderRadius: 4,
  wordBreak: 'break-word',
};

const buttonStyle = {
  background: 'none',
  border: '1px solid var(--warn)',
  borderRadius: 4,
  cursor: 'pointer',
  color: 'inherit',
  fontFamily: 'var(--font-mono)',
  fontSize: 12,
  padding: '2px 8px',
  flexShrink: 0,
};

export default function SaveConflictBanner() {
  const [visible, setVisible] = useState(false);
  const [conflicts, setConflicts] = useState([]);

  useEffect(() => {
    const onConflict = (ev) => {
      setConflicts(Array.isArray(ev?.detail?.conflicts) ? ev.detail.conflicts : []);
      setVisible(true);
    };
    const onResolved = (ev) => {
      setConflicts((prev) => {
        const next = prev.filter((c) => c !== ev?.detail?.conflict);
        if (!next.length) setVisible(false);
        return next;
      });
    };
    window.addEventListener('seasuite:state-conflict', onConflict);
    window.addEventListener('seasuite:state-conflict-resolved', onResolved);
    // A clash overtaken by a newer edit is withdrawn the same way: it no
    // longer offers a choice that could be applied.
    window.addEventListener('seasuite:state-conflict-withdrawn', onResolved);
    return () => {
      window.removeEventListener('seasuite:state-conflict-withdrawn', onResolved);
      window.removeEventListener('seasuite:state-conflict', onConflict);
      window.removeEventListener('seasuite:state-conflict-resolved', onResolved);
    };
  }, []);

  const dismiss = useCallback(() => {
    setVisible(false);
    setConflicts([]);
  }, []);

  const keepMine = useCallback((conflict) => {
    window.dispatchEvent(new CustomEvent('seasuite:conflict-keep-mine', { detail: { conflict } }));
  }, []);

  // Theirs is already what is stored; choosing it only closes this row.
  const keepTheirs = useCallback((conflict) => {
    window.dispatchEvent(
      new CustomEvent('seasuite:state-conflict-resolved', { detail: { conflict } })
    );
  }, []);

  if (!visible) return null;

  return (
    <div
      role="alert"
      style={{
        background: 'var(--warn-bg)',
        borderBottom: '1px solid var(--warn)',
        color: 'var(--warn)',
        padding: '8px 20px',
        fontSize: '13px',
        fontFamily: 'var(--font-mono)',
        zIndex: 200,
        flexShrink: 0,
        pointerEvents: 'auto',
      }}
    >
      <div
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}
      >
        <span>
          {/* The headline is what every playscript watches for — keep it. */}
          <span style={{ fontWeight: 600 }}>Workspace updated elsewhere</span>
          {conflicts.length
            ? ' — someone changed the same thing at the same time. Everything else you changed was saved. For the field' +
              (conflicts.length > 1 ? 's' : '') +
              ' below, their value is kept unless you choose yours.'
            : ' — this page was changed in another session and has been reloaded. ' +
              'Any unsaved changes you just made were not saved — please review and re-apply them.'}
        </span>
        <button
          onClick={dismiss}
          aria-label="Dismiss conflict notice"
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            color: 'inherit',
            fontSize: 18,
            lineHeight: 1,
            padding: '0 4px',
            opacity: 0.7,
            flexShrink: 0,
          }}
        >
          ×
        </button>
      </div>
      {conflicts.length > 0 && (
        <ul style={{ listStyle: 'none', margin: '6px 0 0', padding: 0, display: 'grid', gap: 6 }}>
          {conflicts.map((c, i) => (
            <li
              key={i}
              data-testid="save-conflict-field"
              style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}
            >
              <span style={{ fontWeight: 600 }}>{c.label}</span>
              <span>
                theirs <span style={valueStyle}>{c.deleted ? '(deleted)' : show(c.theirs)}</span>
              </span>
              <span>
                yours <span style={valueStyle}>{show(c.mine)}</span>
              </span>
              {c.overridable ? (
                <button style={buttonStyle} onClick={() => keepMine(c)}>
                  {ACTION_LABEL[c.action] || 'Keep mine'}
                </button>
              ) : (
                <span style={{ opacity: 0.8 }}>{c.notOverridableBecause}</span>
              )}
              {c.note && <span style={{ opacity: 0.8 }}>{c.note}</span>}
              <button style={buttonStyle} onClick={() => keepTheirs(c)}>
                {c.deleted ? 'Let the delete stand' : 'Keep theirs'}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
